/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React from 'react';
import {ImageSizes, MaxThumbnailSize, selectImageBasePath, selectRendition} from "../../data/aem.model";
import {getResource} from "../../selectors/generic";
import {detectIE} from "../../utils";
import {Picture} from "./layout/Picture";
import {ProductsInCartOverlay} from "../molecules/finder/ProductsInCartOverlay";

const defaultSrcSet = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const isIE = detectIE();

const Image = ({image, altText, hasAltImage, position = "card-image-middle-aligned", className}) => {

    if (!image) {
        return null;
    }

    const css = ["lazyload", position, image.cssClass, className, hasAltImage ? "thumbnail__with-packshot" : null].filter(s => s).join(" ");

    if (typeof image === "string") {
        return (<Picture>
            <img src={getResource(image) || defaultSrcSet} alt={altText} className={css}/>
        </Picture>);
    } else if (isIE) {
        return <img src={getResource(selectRendition(image))} alt={altText} className={css + " withFallback"}/>;
    }

    let srcSet = ImageSizes.filter(size => image[(size <= MaxThumbnailSize ? 'thumbnail' : 'rendition') + size + "Path"])
        .map(size => image[(size <= MaxThumbnailSize ? 'thumbnail' : 'rendition') + size + "Path"] + ' ' + size + 'w');

    // For backward compatibility, if no rendition can be found then use the fallback image which is stored in the field {image#renditionOriginalPath}
    if (!srcSet.length) {
        srcSet = Array(1).fill(image["renditionOriginalPath"]);
    }

    return <img className={css}
                alt={altText}
                src={getResource(selectImageBasePath(image))}
                srcSet={defaultSrcSet}
                data-sizes="auto"
                data-srcset={srcSet}/>
}

export const ThumbnailFigure = ({
                                    link, image, altImage, position, altText, children, useAltImage, showReferenceImageFirst,
                                    materialNumbers = undefined, hideOverlay = false, addEvent = undefined,
                                    productClickData = undefined, listPosition = 1,
                                }) => {

    let imageRendition = selectRendition(image, 128);
    let altImageRendition = selectRendition(altImage, 128);

    return <figure className="thumbnail__figure">
        {children}
        <a href={link} onClick={() => addEvent(productClickData, listPosition)}
           onMouseDown={(e) => e.button === 1 && addEvent(productClickData, listPosition)}>
            {!hideOverlay && materialNumbers && !altImageRendition && <ProductsInCartOverlay id={materialNumbers} fading/>}
            {imageRendition && <div
                className={`thumbnail__packshot bg-lazyload lazyload withFallback${!useAltImage || !altImageRendition ? " no-opacity" : ""} ${showReferenceImageFirst ? "thumbnail__reference-image-first" : ""}`}
                style={{backgroundImage: "url('" + getResource(imageRendition) + "')"}}>
                {!hideOverlay && materialNumbers && altImageRendition && <ProductsInCartOverlay id={materialNumbers} fading/>}
            </div>}
            {useAltImage && altImageRendition &&
            <Image image={altImage} altText={altText} hasRendition={!!altImageRendition}
                   hasAltImage={!!imageRendition} position={position}/>}
            {!imageRendition && !altImageRendition && <Picture><img src={defaultSrcSet} alt={altText}/></Picture>}
        </a>
    </figure>;
};
